import {APP_INITIALIZER, ErrorHandler, NgModule} from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import {Router, RouteReuseStrategy} from "@angular/router";
import { IonicModule, IonicRouteStrategy } from "@ionic/angular";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { getApp, initializeApp, provideFirebaseApp } from "@angular/fire/app";
import {
  connectAuthEmulator,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  provideAuth,
} from "@angular/fire/auth";
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  provideFirestore,
} from "@angular/fire/firestore";
import {
  connectFunctionsEmulator,
  getFunctions,
  provideFunctions,
} from "@angular/fire/functions";
import { environment } from "src/environments/environment";
import { LoginModule } from "./login/login.module";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { HttpClient, HttpClientModule } from "@angular/common/http";
import { Capacitor } from "@capacitor/core";
import {
  connectStorageEmulator,
  getStorage,
  provideStorage,
} from "@angular/fire/storage";
import { provideLottieOptions } from "ngx-lottie";
import { provideAnimations } from "@angular/platform-browser/animations";
import localePt from "@angular/common/locales/pt";
import { registerLocaleData } from "@angular/common";
import * as Sentry from "@sentry/angular";

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, "assets/i18n/", ".json");
}
registerLocaleData(localePt);

if (environment.production) {
  Sentry.init({
    dsn: "https://742ed556b64d7349e2b386a53d81c48a@o4509572203806720.ingest.us.sentry.io/4509582346944512",
    sendDefaultPii: true,
    tracePropagationTargets: [/^https?:\/\//]
  });
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    LoginModule,
    IonicModule.forRoot(),
    AppRoutingModule,
    HttpClientModule,
    TranslateModule.forRoot({
      defaultLanguage: "en-CA",
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
      isolate: false,
    }),
  ],
  providers: [
    provideFirebaseApp(() => {
      const app = initializeApp(environment.firebaseConfig);
      initializeFirestore(app, {
        localCache: persistentLocalCache({ cacheSizeBytes: 40000000 }),
      });
      return app;
    }),
    provideAuth(() => {
      let auth;
      if (Capacitor.isNativePlatform()) {
        auth = initializeAuth(getApp(), {
          persistence: indexedDBLocalPersistence,
        });
      } else {
        auth = getAuth();
      }
      if (environment.useEmulator) {
        connectAuthEmulator(auth, `http://${environment.emulatorHost}:9099`, {
          disableWarnings: true,
        });
      }
      return auth;
    }),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (environment.useEmulator) {
        connectFirestoreEmulator(firestore, environment.emulatorHost, 8089);
      }
      return firestore;
    }),
    provideFunctions(() => {
      const functions = getFunctions();
      if (environment.useEmulator) {
        connectFunctionsEmulator(functions, environment.emulatorHost, 5001);
      }
      return functions;
    }),
    provideStorage(() => {
      const storage = getStorage();
      if (environment.useEmulator) {
        connectStorageEmulator(storage, environment.emulatorHost, 9199);
      }
      return storage;
    }),
    provideAnimations(),
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy,
    },
    provideLottieOptions({
      player: () => import("lottie-web"),
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    }, {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
